import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    setSelectedPlaylist,
    setVariant,
    clearResults,
    LIKES_SOURCE_ID,
} from '../../redux/slices/smartRecsSlice';
import { fetchPlaylistRecommendations } from '../../redux/thunk/smartRecsThunk';
import { addToNextUp, fullFirstQueueSrc } from '../../utils/queueAdd';
import { playAllTracks } from '../../utils/playAllTracks';
import PlayAllButton from '../common/PlayAllButton';
import { useToast } from '../../contexts/ToastContext';
import ErrorBanner from '../ErrorBanner';
import TrackContextMenu from '../TrackContextMenu';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import DJPalProgressBar from '../DJPal/DJPalProgressBar';
import DJPalScrollableList from '../DJPal/DJPalScrollableList';
import DJPalAudioPreview from '../DJPal/DJPalAudioPreview';
import { delayedClick } from '../DJPal/MixPlayer';
import { goTrack } from '../DJPal/mixpediaHomeData';
import { setActiveSearch, setTrackQuery } from '../../redux/slices/djpalSlice';
import { setActiveTab } from '../../redux/slices/appSlice';
import useDjpalConnect from '../../hooks/useDjpalConnect';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import LikeButton from '../LikeButton';
import { useTierGate } from '../../hooks/useTierGate';
import { track as trackEvent } from '../../utils/analytics';
import { GoatBadge } from '../Tier';
import { isWeb as _isWeb, notifyWebOnly } from '../../utils/webOnly';
import { isRecsVariantDevEnabled } from '../../utils/devFlags';
import { useIsMobileWeb } from '../../hooks/useIsMobileWeb';
import MobileListSearch, { matchesQuery } from '../common/MobileListSearch';
import { DEFAULT_PLAYLIST_COVER, playlistCoverUrl } from '../../utils/cover';
import { safeArtwork } from '../../utils/no1001';
import { useDesignV2 } from '../../utils/designV2';
import { SectionHeader } from '../v2/PageTabs';
import '../DJPal/DJPal.scss';
import './SmartRecsTab.scss';

const SmartRecsTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const isWeb = _isWeb;
    const { canAccess, requireUpgrade } = useTierGate('smart-recommendations');

    // On web, show CTA hint about desktop playlist integration
    useEffect(() => {
        if (isWeb) {
            setTimeout(() => {
                notifyWebOnly('crate-digger', 'hint');
            }, 500);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [playlists, setPlaylists] = useState([]);
    const [playlistsLoaded, setPlaylistsLoaded] = useState(false);
    const [copiedTrackId, setCopiedTrackId] = useState(null);
    const [playlistExpanded, setPlaylistExpanded] = useState(false);
    const [showAllRecs, setShowAllRecs] = useState(false);
    // Right-click on a recommendation row opens the same menu as its ⋯ button.
    const { openRowCtx, rowCtxFor } = useRowContextMenu();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const isMobileWeb = useIsMobileWeb();
    const containerRef = useRef(null);
    const matchIndexRef = useRef(0);

    const {
        selectedPlaylistId,
        isLoading,
        error,
        results,
        variant,
    } = useSelector((state) => state.smartRecs);

    const accessToken = useSelector((state) => state.auth.accessToken);
    const likesCount = useSelector((s) => s.likes?.total || s.likes?.likedTracks?.length || 0);
    const likesOptionVisible = !searchFilter || 'liked tracks'.includes(searchFilter.toLowerCase());

    // Background DJPal token resolution — see useDjpalConnect for rationale.
    const { connectFailed, tokenError, retry: retryConnect } = useDjpalConnect();

    // Load playlists on mount AND on every dropdown open. Mirror the sidebar
    // (PlaylistSidebar): when the user is signed in, their playlists live in
    // the cloud library — use those regardless of platform. Only fall back to
    // local SQLite when signed out (desktop-only). Previously this keyed off
    // `isWeb`, so a signed-in desktop user always read empty local SQLite and
    // saw only "Liked Tracks".
    //
    // The dropdown-open reload matters on packaged cold start: the first
    // fetch can fail (network not up yet, or a 401 from a stale restored
    // token) and `result.data` is then undefined — without a reload the
    // picker silently shows only "Liked Tracks" until the token refresh
    // happens to re-fire this effect minutes later. On failure we KEEP the
    // previous list rather than blanking it.
    const loadPlaylists = useCallback(async () => {
        try {
            if (accessToken) {
                // refreshAndRetry: a cold-start 401 (restored token already
                // expired) refreshes and retries instead of failing silently.
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                if (result?.error) {
                    console.warn('getPlaylists failed (keeping current list):', result.error);
                    return;
                }
                const data = result?.data || [];
                setPlaylists(data.map(p => ({ ...p, trackCount: p.track_count })));
            } else if (!isWeb) {
                const all = await window.electron.db.playlists.getAll(false);
                setPlaylists(all || []);
            } else {
                setPlaylists([]);
            }
        } catch (err) {
            console.error('Failed to load playlists:', err);
        } finally {
            setPlaylistsLoaded(true);
        }
    }, [accessToken, isWeb]);

    useEffect(() => {
        loadPlaylists();
    }, [loadPlaylists]);

    // Refresh the list each time the picker opens (same pattern as the
    // tool-tab playlist pickers) so a failed cold-start fetch never leaves
    // the dropdown stuck at "Liked Tracks" only.
    useEffect(() => {
        if (dropdownOpen) loadPlaylists();
    }, [dropdownOpen, loadPlaylists]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            // The phone search overlay is portaled to <body> — taps inside it
            // are NOT "outside" the picker (closing would unmount the overlay).
            if (e.target.closest && e.target.closest('.mobile-search-overlay')) return;
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
                setSearchFilter('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens. Desktop only: on phones that
    // summons the keyboard the instant the picker opens, squeezing the
    // playlist list the user came to tap. They tap the field to search.
    useEffect(() => {
        if (dropdownOpen && !isMobileWeb && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [dropdownOpen, isMobileWeb]);

    const handlePlaylistSelect = useCallback((id) => {
        dispatch(setSelectedPlaylist(id));
        dispatch(clearResults());
        setPlaylistExpanded(false);
        setShowAllRecs(false);
        setDropdownOpen(false);
        setSearchFilter('');
    }, [dispatch]);

    const handleGetRecommendations = useCallback(() => {
        if (!selectedPlaylistId) return;
        dispatch(fetchPlaylistRecommendations({ playlistId: selectedPlaylistId, variant }));
    }, [dispatch, selectedPlaylistId, variant]);

    const handleVariantSwitch = useCallback((v) => {
        dispatch(setVariant(v));
        if (selectedPlaylistId) {
            dispatch(fetchPlaylistRecommendations({ playlistId: selectedPlaylistId, variant: v }));
        }
    }, [dispatch, selectedPlaylistId]);

    const handleCopyTrack = useCallback((e, track, trackId) => {
        e.stopPropagation();
        e.preventDefault();
        const text = track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        navigator.clipboard.writeText(text).catch(() => {});
        setCopiedTrackId(trackId);
        setTimeout(() => setCopiedTrackId(null), 1500);
    }, []);

    const handleAddToQueue = useCallback((e, track) => {
        e.stopPropagation();
        e.preventDefault();
        // Full-first like every play path (was the 30s sample only).
        const src = fullFirstQueueSrc(track);
        if (!src) return;
        const art = safeArtwork(track.cover || track.artwork || track.artworkUrl || track.image) || '';
        addToNextUp(dispatch, {
            name: track.title || 'Unknown Track',
            singer: track.artist || '',
            cover: art,
            ...src,
        });
    }, [dispatch]);

    // PlayAllButton owns the busy-guard, spinner, and pause/resume state —
    // this just builds the playable list and starts it (returns bool).
    const handlePlayAll = useCallback(async () => {
        const recs = results?.recommendations || [];
        if (recs.length === 0) return false;

        const playable = recs
            .map(r => r.track)
            .filter(t => t && (t.previewUrl || t.sampleUrl || t.preview_url || t.preview || t.youtubeId || t.soundcloudUrl));

        if (playable.length === 0) {
            toast.warning('No playable recommendations');
            return false;
        }

        // Resolve only the FIRST track and hand the whole list to the
        // listContext — advanceListContext lazy-resolves each next track as
        // the queue advances. The old version bulk-resolved every rec
        // (2000-track playlists → 2000 background resolves).
        const ok = await playAllTracks(dispatch, playable);
        if (!ok) toast.warning('Could not load first track');
        return ok;
    }, [dispatch, results, toast]);

    // Row click → the track's Mixpedia page. goTrack fires `track_opened`
    // and runs the track search; we only add the tab switch since Crate
    // Digger lives outside the Mixpedia tab. Order matters for Back-nav
    // (same trap as RowActions.searchInMixpedia): the 'track' sub-tab and
    // query must be set BEFORE setActiveTab('djpal') so the nav-history
    // marker pushed on the tab switch is a clean 'track' marker the search
    // result then REPLACES — one Back returns here, not to Mixpedia home.
    const handleOpenTrack = useCallback((track) => {
        const q = track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        if (!q) return;
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(q));
        dispatch(setActiveTab('djpal'));
        goTrack(dispatch, track, 'crate_digger');
    }, [dispatch]);

    // Find-in-page (Cmd/Ctrl+F): scroll to the next/prev matching row on
    // Enter. The FindOverlay only paints CSS highlights + fires
    // 'search-next-match'; without this listener Crate Digger highlighted
    // matches but never scrolled to them. Walk the rendered rows (source
    // tracks + recommendations) at event time — same approach as FeedTab.
    const searchTerm = useSelector((state) => state.app.searchTerm);
    const findMatchRows = useCallback(() => {
        const container = containerRef.current;
        if (!container || !searchTerm) return [];
        const term = searchTerm.toLowerCase();
        const rows = [];
        for (const row of container.querySelectorAll('.djpal-track-row, .source-track-row')) {
            if (row.textContent.toLowerCase().includes(term)) rows.push(row);
        }
        return rows;
    }, [searchTerm]);

    // Auto-jump to the first match as the user types — matches real browser
    // find (and the Mixpedia search tabs), rather than waiting for Enter.
    // Deferred a frame so freshly-rendered rows are in the DOM first.
    useEffect(() => {
        matchIndexRef.current = 0;
        if (!searchTerm) return;
        const raf = requestAnimationFrame(() => {
            findMatchRows()[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return () => cancelAnimationFrame(raf);
    }, [searchTerm, findMatchRows]);

    // Enter / Shift+Enter step to the next/prev match.
    useEffect(() => {
        const handleNextMatch = (e) => {
            const matchEls = findMatchRows();
            if (!matchEls.length) return;
            matchIndexRef.current = (matchIndexRef.current + e.detail + matchEls.length) % matchEls.length;
            matchEls[matchIndexRef.current]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        window.addEventListener('search-next-match', handleNextMatch);
        return () => window.removeEventListener('search-next-match', handleNextMatch);
    }, [findMatchRows]);

    // Free plan walls (decided by main / the server, never here): a top-N cut
    // with the real remainder, or today's playlist runs used up.
    const freeLimit = results?.freeLimit || null;
    const dailyLocked = Boolean(results?.locked);
    useEffect(() => {
        if (freeLimit && freeLimit.moreCount > 0) trackEvent('free_limit_hit', { feature: 'crate_digger', kind: 'picks', more_count: freeLimit.moreCount, runs_used: freeLimit.runs?.runsUsed });
        else if (dailyLocked) trackEvent('free_limit_hit', { feature: 'crate_digger', kind: 'runs', runs_used: results?.runs?.runsUsed });
    }, [freeLimit, dailyLocked]); // eslint-disable-line react-hooks/exhaustive-deps
    const allRecommendations = results?.recommendations || [];
    const INITIAL_RECS_LIMIT = 50;
    const recommendations = showAllRecs ? allRecommendations : allRecommendations.slice(0, INITIAL_RECS_LIMIT);
    const hasMoreRecs = allRecommendations.length > INITIAL_RECS_LIMIT;

    // Mobile (mobile-web + iOS/Android native) — let the recommendations
    // container grow to its natural height so the WHOLE PAGE scrolls,
    // instead of trapping the list in a cramped fixed-height inner-scroll
    // box. Desktop keeps the bounded box (it sits in a multi-pane layout
    // where an inner scroll is the right pattern). isWeb is true on
    // mobile-web AND native; the matchMedia gate excludes desktop-web
    // viewed in a large browser window. On mobile we pass 'none' (NOT
    // undefined) so the inline style overrides the stylesheet's
    // .djpal-scrollable-list { max-height: 320px } — without it the list
    // would just fall back to that 320px cap and inner-scroll. With
    // max-height:none the list grows to its natural height and the page
    // (.lower-part) scrolls instead.
    const isMobileViewport = isWeb
        && typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 768px)').matches;
    const recsMaxHeight = isMobileViewport ? 'none' : 'calc(100vh - 380px)';
    const sourcePlaylist = results?.sourcePlaylist;
    const sourceTracks = sourcePlaylist?.tracks || [];

    // DESIGN V2 — source chips + one filled "Dig" button, a headline with the
    // match count, and a one-line reason per pick. The playlist picker menu is
    // the same element in both designs; only its trigger changes.
    const v2 = useDesignV2();
    const selectedPlaylist = selectedPlaylistId && selectedPlaylistId !== LIKES_SOURCE_ID
        ? playlists.find(p => p.id === selectedPlaylistId) : null;
    const dropdownMenu = dropdownOpen && (
                                <div className="smart-recs-dropdown-menu">
                                    {isMobileWeb ? (
                                        <div className="dropdown-search-wrapper dropdown-search-wrapper--mobile">
                                            <MobileListSearch
                                                inputRef={searchInputRef}
                                                value={searchFilter}
                                                onChange={setSearchFilter}
                                                placeholder="Search playlists"
                                                ariaLabel="Search playlists"
                                                onKeyDown={(e) => e.stopPropagation()}
                                                overlay={{
                                                    fetch: (q) => [
                                                        ...(matchesQuery('liked tracks', q)
                                                            ? [{ key: 'likes', title: '♥ Liked Tracks', subtitle: likesCount ? `${likesCount} tracks` : 'your likes', artworkUrl: DEFAULT_PLAYLIST_COVER, item: LIKES_SOURCE_ID }]
                                                            : []),
                                                        ...playlists
                                                            .filter(pl => matchesQuery(pl.name, q))
                                                            .map(pl => ({
                                                                key: `pl-${pl.id}`,
                                                                title: pl.name,
                                                                subtitle: `${pl.trackCount ?? pl.tracks?.length ?? 0} tracks`,
                                                                artworkUrl: playlistCoverUrl(pl),
                                                                item: pl.id,
                                                            })),
                                                    ],
                                                    onSelect: (row) => handlePlaylistSelect(row.item),
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="dropdown-search-wrapper">
                                            <svg className="dropdown-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                            <input
                                                ref={searchInputRef}
                                                className="dropdown-search-input"
                                                type="text"
                                                placeholder="Search playlists..."
                                                value={searchFilter}
                                                onChange={(e) => setSearchFilter(e.target.value)}
                                                onKeyDown={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    )}
                                    <div className="dropdown-options">
                                        {likesOptionVisible && (
                                            <button
                                                className={`dropdown-option dropdown-option-likes${selectedPlaylistId === LIKES_SOURCE_ID ? ' selected' : ''}`}
                                                onClick={() => handlePlaylistSelect(LIKES_SOURCE_ID)}
                                            >
                                                <span className="dropdown-option-name">♥ Liked Tracks</span>
                                                <span className="dropdown-option-count">{likesCount ? `${likesCount} tracks` : 'your likes'}</span>
                                            </button>
                                        )}
                                        {playlists
                                            .filter(pl => !searchFilter || pl.name.toLowerCase().includes(searchFilter.toLowerCase()))
                                            .map((pl) => (
                                                <button
                                                    key={pl.id}
                                                    className={`dropdown-option${pl.id === selectedPlaylistId ? ' selected' : ''}`}
                                                    onClick={() => handlePlaylistSelect(pl.id)}
                                                >
                                                    <span className="dropdown-option-name">{pl.name}</span>
                                                    <span className="dropdown-option-count">{pl.trackCount ?? pl.tracks?.length ?? 0} tracks</span>
                                                </button>
                                            ))
                                        }
                                        {!likesOptionVisible && playlists.filter(pl => !searchFilter || pl.name.toLowerCase().includes(searchFilter.toLowerCase())).length === 0 && (
                                            <div className="dropdown-no-results">No playlists found</div>
                                        )}
                                    </div>
                                </div>
    );
    // "151 of your 155 liked tracks are played by DJs in Mixpedia" — the most
    // interesting number on the page, said once, in a sentence.
    const v2Headline = (() => {
        if (!v2 || !results || dailyLocked) return null;
        const found = results.sourceTracksFound;
        if (found == null || !sourceTracks.length) return null;
        const what = sourcePlaylist?.id === LIKES_SOURCE_ID || selectedPlaylistId === LIKES_SOURCE_ID
            ? `of your ${sourceTracks.length} liked tracks`
            : `of the ${sourceTracks.length} tracks in ${sourcePlaylist?.name || 'this crate'}`;
        return `${found} ${what} ${found === 1 ? 'is' : 'are'} played by DJs in Mixpedia`;
    })();

    return (
        <div className="tab-container smart-recs-tab" ref={containerRef}>
            <div className="lower-part">
                {connectFailed && (
                    <div className="connect-banner">
                        <span>Connection failed: {tokenError}</span>
                        <button className="connect-banner-retry" onClick={retryConnect}>Retry</button>
                    </div>
                )}
                {v2 ? (
                <div className="smart-recs-controls v2-crate-controls">
                    <span className="v2-faint v2-crate-from">Dig from</span>
                    <button
                        type="button"
                        className={`v2-chip${selectedPlaylistId === LIKES_SOURCE_ID ? ' on' : ''}`}
                        onClick={() => !isLoading && handlePlaylistSelect(LIKES_SOURCE_ID)}
                        disabled={isLoading}
                    >
                        ♥ Liked tracks{likesCount ? <small>· {likesCount}</small> : null}
                    </button>
                    <div className={`smart-recs-dropdown v2-crate-picker${dropdownOpen ? ' open' : ''}${isLoading ? ' disabled' : ''}`} ref={dropdownRef}>
                        <button
                            type="button"
                            className={`v2-chip${selectedPlaylist ? ' on' : ''}`}
                            onClick={() => !isLoading && setDropdownOpen(!dropdownOpen)}
                            disabled={isLoading}
                        >
                            {selectedPlaylist ? selectedPlaylist.name : 'Playlist'}
                            {selectedPlaylist && <small>· {selectedPlaylist.trackCount ?? selectedPlaylist.tracks?.length ?? 0}</small>}
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .6 }}><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {dropdownMenu}
                    </div>
                    <span className="v2-crate-spacer" />
                    {isRecsVariantDevEnabled() && (
                        <div className="djpal-variant-toggle">
                            <button className={variant === 'B' ? 'active' : ''} onClick={() => handleVariantSwitch('B')}>V8</button>
                            <button className={variant === 'A' ? 'active' : ''} onClick={() => handleVariantSwitch('A')}>V9</button>
                        </div>
                    )}
                    <button
                        type="button"
                        className="v2-btn primary"
                        onClick={handleGetRecommendations}
                        disabled={!selectedPlaylistId || isLoading}
                    >
                        {isLoading ? 'Digging…' : 'Dig'}
                    </button>
                </div>
                ) : (
                <div className="smart-recs-controls">
                    <div className="smart-recs-selector-row">
                        <div className={`smart-recs-dropdown${dropdownOpen ? ' open' : ''}${isLoading ? ' disabled' : ''}`} ref={dropdownRef}>
                            <button
                                className="smart-recs-dropdown-trigger"
                                onClick={() => !isLoading && setDropdownOpen(!dropdownOpen)}
                                disabled={isLoading}
                            >
                                <span className={`dropdown-trigger-text${!selectedPlaylistId ? ' is-empty' : ''}`}>
                                    {selectedPlaylistId === LIKES_SOURCE_ID
                                        ? (likesCount ? `Liked Tracks (${likesCount} tracks)` : 'Liked Tracks')
                                        : selectedPlaylistId
                                            ? (() => { const pl = playlists.find(p => p.id === selectedPlaylistId); return pl ? `${pl.name} (${pl.trackCount ?? pl.tracks?.length ?? 0} tracks)` : 'Select a source...'; })()
                                            : 'Select a source...'}
                                </span>
                            </button>
                            {dropdownMenu}
                        </div>
                        <button
                            className="smart-recs-go-btn"
                            onClick={handleGetRecommendations}
                            disabled={!selectedPlaylistId || isLoading}
                        >
                            {isLoading ? 'Loading...' : 'Get Recommendations'}
                        </button>
                        {/* V8/V9 dev toggle — gate shared with the track-search
                            variant-B prefetch (see utils/devFlags.js). */}
                        {isRecsVariantDevEnabled() && (
                            <div className="djpal-variant-toggle">
                                <button className={variant === 'B' ? 'active' : ''} onClick={() => handleVariantSwitch('B')}>V8</button>
                                <button className={variant === 'A' ? 'active' : ''} onClick={() => handleVariantSwitch('A')}>V9</button>
                            </div>
                        )}
                    </div>
                </div>
                )}

                {v2Headline && (
                    <div className="v2-crate-headline">
                        <div className="v2-crate-headline-text">{v2Headline}</div>
                        {allRecommendations.length > 0 && <div className="v2-faint">Here is what those DJs play next to them.</div>}
                    </div>
                )}

                {v2 && !results && !isLoading && !error && (
                    <div className="v2-crate-ready">
                        <div className="v2-crate-ready-title">Pick a crate to dig from</div>
                        <div className="v2-faint">Choose a playlist or your likes. MixGoat looks through real DJ sets for the records played right before and after yours, and hands you the ones that fit.</div>
                        <div className="v2-crate-tools">
                            <input type="search" className="v2-crate-search" placeholder="Filter crates" value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} aria-label="Filter crates" />
                        </div>
                        <div className="v2-crate-grid">
                            <button type="button" className="v2-crate-tile" onClick={() => { handlePlaylistSelect(LIKES_SOURCE_ID); dispatch(fetchPlaylistRecommendations({ playlistId: LIKES_SOURCE_ID, variant })); }}>
                                <span className="v2-crate-tile-art is-likes" aria-hidden="true">♥</span>
                                <span className="v2-crate-tile-name">Liked tracks</span>
                                <span className="v2-crate-tile-meta v2-num">{likesCount ? `${likesCount} tracks` : 'your likes'}</span>
                            </button>
                            {!playlistsLoaded && [0, 1, 2, 3, 4, 5].map((i) => <span key={`sk-${i}`} className="v2-crate-tile v2-crate-skel" aria-hidden="true"><span className="v2-crate-tile-art" /><span className="v2-skel-line" /><span className="v2-skel-line sm" /></span>)}
                            {playlists.filter((pl) => (pl.trackCount ?? pl.tracks?.length ?? 0) > 0 && (!searchFilter || pl.name.toLowerCase().includes(searchFilter.toLowerCase()))).map((pl) => {
                                const n = pl.trackCount ?? pl.tracks?.length ?? 0;
                                const cover = playlistCoverUrl(pl);
                                return (
                                    <button type="button" key={pl.id} className="v2-crate-tile" title={pl.name} onClick={() => { handlePlaylistSelect(pl.id); dispatch(fetchPlaylistRecommendations({ playlistId: pl.id, variant })); }}>
                                        {cover && cover !== DEFAULT_PLAYLIST_COVER
                                            ? <span className="v2-crate-tile-art" style={{ backgroundImage: `url("${cover}")` }} aria-hidden="true" />
                                            : <span className="v2-crate-tile-art is-empty" aria-hidden="true" />}
                                        <span className="v2-crate-tile-name">{pl.name}</span>
                                        <span className="v2-crate-tile-meta v2-num">{n} {n === 1 ? 'track' : 'tracks'}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                <DJPalProgressBar visible={isLoading} />

                {error && (
                    <ErrorBanner error={error} surface="smart_recs" />
                )}

                {!isLoading && !error && results && !dailyLocked && allRecommendations.length === 0 && (() => {
                    const selectedPl = playlists.find(p => p.id === selectedPlaylistId);
                    const trackCount = selectedPl?.trackCount ?? selectedPl?.tracks?.length ?? 0;
                    const isSmall = trackCount > 0 && trackCount < 10;
                    // sourceTracksFound === 0 means NONE of the crate's tracks
                    // resolved on Mixpedia (the server never even reached the
                    // recs engine) — a matching problem, not a dry crate. Say
                    // so instead of the misleading "came up dry".
                    const matchedNone = (results.sourceTracksFound ?? 0) === 0;
                    return (
                        <div className="smart-recs-empty">
                            <div className="smart-recs-empty-title">
                                {matchedNone ? "Couldn't match this crate" : 'Crate came up dry'}
                            </div>
                            <div className="smart-recs-empty-body">
                                {matchedNone ? (
                                    <>None of this crate's tracks could be matched on Mixpedia, so there was nothing to dig from. This can happen with unreleased edits and IDs — or let us know so we can look into it.</>
                                ) : isSmall ? (
                                    <>This crate only has <strong>{trackCount}</strong> track{trackCount === 1 ? '' : 's'} — try a bigger one and we'll have more to dig through.</>
                                ) : (
                                    <>Couldn't surface anything fresh for this one. Try a different playlist — more variety usually shakes loose better picks.</>
                                )}
                            </div>
                            <button
                                className="smart-recs-empty-cta"
                                onClick={() => setDropdownOpen(true)}
                            >
                                Try another playlist
                            </button>
                        </div>
                    );
                })()}

                {results && (recommendations.length > 0 || sourcePlaylist) && (
                    <div className="djpal-results smart-recs-results">
                        {/* Source playlist section */}
                        {sourcePlaylist && (
                            <div className="smart-recs-source">
                                <button
                                    className="source-header"
                                    onClick={() => setPlaylistExpanded(!playlistExpanded)}
                                >
                                    <div className="source-header-left">
                                        <svg className="source-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: playlistExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                            <polyline points="9 18 15 12 9 6"/>
                                        </svg>
                                        <span className="source-title">{sourcePlaylist.name}</span>
                                        <span className="source-count">{sourceTracks.length} tracks</span>
                                    </div>
                                    <div className="source-header-right">
                                        {results.sourceTracksFound > 0 && (
                                            <span className="source-badge source-badge-found">{results.sourceTracksFound} matched</span>
                                        )}
                                    </div>
                                </button>
                                {playlistExpanded && (
                                    <div className="source-tracks">
                                        {sourceTracks.map((t, i) => {
                                            const label = t.artist && t.title
                                                ? `${t.artist} - ${t.title}`
                                                : t.title || t.fileName.replace(/\.[^/.]+$/, '');
                                            const hasSrc = !!t.filePath;
                                            return (
                                                <div
                                                    className="source-track-row"
                                                    key={i}
                                                    {...delayedClick(() => handleOpenTrack(t))}
                                                >
                                                    <span className="source-track-num">{i + 1}</span>
                                                    <DJPalAudioPreview
                                                        src={hasSrc ? t.filePath : null}
                                                        previewId={`src-${i}`}
                                                        trackName={t.title}
                                                        trackArtist={t.artist}
                                                        trackCover={t.cover}
                                                        playContext={{ items: sourceTracks.map(st => ({ ...st, artwork: st.cover })), index: i, type: 'tracks' }}
                                                    >
                                                        <div className="source-track-art">
                                                            {t.cover
                                                                ? <img src={safeArtwork(t.cover)} alt={t.title} />
                                                                : <div className="source-art-placeholder">&#9835;</div>
                                                            }
                                                        </div>
                                                    </DJPalAudioPreview>
                                                    <span className="source-track-name">{label}</span>
                                                    <div className="source-track-actions">
                                                        <button
                                                            className="track-copy-btn"
                                                            title="Copy track name"
                                                            onClick={(e) => handleCopyTrack(e, t, `src-${i}`)}
                                                        >
                                                            {copiedTrackId === `src-${i}` ? (
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                            ) : (
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                                                            )}
                                                        </button>
                                                        {hasSrc && (
                                                            <button
                                                                className="track-queue-btn"
                                                                title="Add to queue"
                                                                onClick={(e) => handleAddToQueue(e, t)}
                                                            ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg></button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {dailyLocked && (
                            <div className="free-steps-wall crate-wall" data-reason={results.reason}>
                                <div className="free-steps-wall-text">
                                    <strong>You&apos;ve used today&apos;s {results.runs?.runsLimit ?? 3} free playlists</strong>
                                    <span>Come back tomorrow, or unlock unlimited Crate Digger with GOAT.</span>
                                </div>
                                <button type="button" className="free-wall-btn" onClick={requireUpgrade}>Unlock with <GoatBadge /></button>
                            </div>
                        )}
                        {/* Recommendations */}
                        {recommendations.length > 0 && (
                            <>
                                {v2 ? (
                                <SectionHeader
                                    title={freeLimit
                                        ? `Top ${freeLimit.shown} picks`
                                        : (showAllRecs || !hasMoreRecs ? 'Picks' : `Top ${INITIAL_RECS_LIMIT} picks`)}
                                    count={freeLimit
                                        ? `of ${freeLimit.total} · free plan`
                                        : (showAllRecs || !hasMoreRecs ? allRecommendations.length : `of ${allRecommendations.length}`)}
                                >
                                    <span className="v2-crate-spacer" />
                                    <PlayAllButton
                                        className="smart-recs-play-all-btn v2-btn sm"
                                        onStart={handlePlayAll}
                                        identityItems={(results?.recommendations || []).map(r => r.track)}
                                        from="crate_digger"
                                        label="Play all"
                                        iconSize={14}
                                        title="Play all recommendations"
                                    />
                                </SectionHeader>
                                ) : (
                                <div className="smart-recs-stats-header">
                                    <span>
                                        {freeLimit
                                            ? `Top ${freeLimit.shown} of ${freeLimit.total} picks · free plan`
                                            : (showAllRecs || !hasMoreRecs
                                                ? `${allRecommendations.length} recommendations`
                                                : `Top ${INITIAL_RECS_LIMIT} of ${allRecommendations.length} recommendations`)
                                        }
                                    </span>
                                    <PlayAllButton
                                        className="smart-recs-play-all-btn"
                                        onStart={handlePlayAll}
                                        identityItems={(results?.recommendations || []).map(r => r.track)}
                                        from="crate_digger"
                                        label="Play all"
                                        iconSize={14}
                                        title="Play all recommendations"
                                    />
                                </div>
                                )}

                                <div className="djpal-card">
                                    <DJPalScrollableList maxHeight={recsMaxHeight}>
                                        {recommendations.map((rec, i) => {
                                            const track = rec.track;
                                            const art = safeArtwork(track.artwork || track.artworkUrl || track.image);
                                            const previewSrc = track.previewUrl || track.sampleUrl || track.preview_url || track.preview;
                                            // v2 reason line: the seed track(s) this pick was dug up
                                            // next to (server: recommendedBy = source track names).
                                            const seeds = v2 && Array.isArray(rec.recommendedBy) ? rec.recommendedBy.filter(Boolean) : [];
                                            const reason = seeds.length
                                                ? `Played alongside ${seeds[0]}${seeds.length > 1 ? ` and ${seeds.length - 1} more of your tracks` : ''}`
                                                : null;
                                            const showScore = rec.score != null && (!v2 || rec.score < 0.9);
                                            return (
                                                <div
                                                    className={`djpal-track-row clickable${v2 ? ' v2-row' : ''}`}
                                                    key={`sr-${i}`}
                                                    onContextMenu={openRowCtx(`sr-${i}`)}
                                                    {...delayedClick(() => handleOpenTrack(track))}
                                                >
                                                    <DJPalAudioPreview
                                                        src={previewSrc}
                                                        youtubeId={track.youtubeId}
                                                        soundcloudUrl={track.soundcloudUrl}
                                                        trackId={track.id}
                                                        durationSec={track.durationSec ?? track.duration}
                                                        pageUrl={track.pageUrl}
                                                        previewId={`sr-${i}`}
                                                        trackName={track.title}
                                                        trackArtist={track.artist}
                                                        trackArtists={track.artists}
                                                        trackCover={art}
                                                        playContext={{ items: allRecommendations.map(r => r.track), index: i, type: 'tracks' }}
                                                        playbackBlocked={track.playbackBlocked}
                                                    >
                                                        <div className="track-artwork">
                                                            {art ? <img src={art} alt={track.title} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                        </div>
                                                    </DJPalAudioPreview>
                                                    <div className="track-info">
                                                        <span className="track-label">{track.title}</span>
                                                        {track.artist && <span className="track-sublabel">{track.artist}</span>}
                                                        {reason && <span className="track-sublabel v2-crate-reason" title={seeds.join(', ')}>{reason}</span>}
                                                    </div>
                                                    <div className="track-actions-group">
                                                        {showScore && (
                                                            <div className="track-meta">{Math.round(rec.score * 100)}%</div>
                                                        )}
                                                        <LikeButton track={track} />
                                                        <TrackContextMenu track={track} desktopLike openAtPoint={rowCtxFor(`sr-${i}`)} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </DJPalScrollableList>
                                    {freeLimit && freeLimit.moreCount > 0 && (
                                        <div className="free-taste-wall" data-more={freeLimit.moreCount}>
                                            <div className="free-taste-wall-rows" aria-hidden="true">
                                                {[0, 1, 2, 3].map((i) => (
                                                    <div key={i} className="djpal-track-row free-taste-wall-row">
                                                        <div className="track-artwork" />
                                                        <div className="track-info">
                                                            <span className="ftw-bar ftw-title" />
                                                            <span className="ftw-bar ftw-artist" />
                                                        </div>
                                                        <div className="track-actions-group"><span className="ftw-bar ftw-score" /></div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="free-taste-wall-overlay">
                                                <div className="free-taste-wall-text">
                                                    <strong>{`GOAT found ${freeLimit.moreCount.toLocaleString()} more track${freeLimit.moreCount === 1 ? '' : 's'} for this playlist`}</strong>
                                                    <span>Free plan: the top {freeLimit.shown} picks per playlist, {freeLimit.runs?.runsLimit ?? 3} playlists a day.</span>
                                                </div>
                                                <button type="button" className="free-wall-btn" onClick={() => requireUpgrade({ moreCount: freeLimit.moreCount })}>Unlock with <GoatBadge /></button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {hasMoreRecs && (
                                    <button
                                        className="smart-recs-show-more"
                                        onClick={() => setShowAllRecs(!showAllRecs)}
                                    >
                                        {showAllRecs
                                            ? 'Show top 50'
                                            : `Show all ${allRecommendations.length} recommendations`
                                        }
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SmartRecsTab;
